import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLogger } from '@direct-frontend/modules-logger';
import {
    Checkbox,
    FilterSelectList,
    FilterSelectListProps,
    getSelectValues,
    isListBoxSection,
    ListBoxItem,
    ListBoxBaseOption,
    Radiobox,
    RadioboxItem,
    VStack,
} from '@direct-frontend/components';
import { compact, isString } from '@direct-frontend/stdlib';
import { isMobile } from '@direct-frontend/platform-code';
import { hasValue } from '../lib/helpers/reconcile/hasValue';
import { isValidOperator } from '../lib/helpers/reconcile/isValidOperator';
import { BaseFilterProps, Filter, FilterRenderProps } from '../lib/types/Filters';
import { validateNotEmpty } from './createFilterInput/validate/validateNotEmpty';

/**
 * Пропсы для создания фильтра с мультивыбором
 * @typeParam FilterId идентификатор фильтра
 * @typeParam ValueType тип значений в селекте
 */
interface CreateFilterSelectListProps<FilterId extends string, ValueType extends string>
    extends BaseFilterProps<FilterId> {
    /** Список элементов, из которых можно выбрать значение */
    items: FilterSelectListProps<ValueType>['items'];
    /** Нужно ли ограничить выбор всего одним элементом */
    isSingleSelect?: boolean;
    /**
     * Является ли фильтр скрытым для добавления (значение парсится из урла).
     * Такой фильтр нельзя добавить или отредактировать руками, но его можно удалить.
     */
    isHidden?: boolean;
    /** Флаг отображения переключателя между отображением всех и только выбранных значений */
    showSelectedOnlySwitcher?: boolean;
}

function isValuesOfValueType<ValueType extends string>(
    values: string[],
    items: FilterSelectListProps<ValueType>['items']
): values is ValueType[] {
    const selectValues = getSelectValues(items);

    return values.every(value => selectValues.some(selectValue => selectValue === value));
}

/**
 * Фабрика, создающая фильтр с мультивыбором,
 * который можно передать как Filter в FiltersDeclaration
 * @typeParam FilterId идентификатор фильтра
 * @typeParam ValueType тип значений в селекте
 * @returns Декларация фильтра
 * @kind helper
 */
export function createFilterSelectList<FilterId extends string, ValueType extends string>({
    id,
    items,
    isSingleSelect = false,
    ...filterProps
}: CreateFilterSelectListProps<FilterId, ValueType>): Filter<FilterId, ValueType[]> {
    return {
        ...filterProps,
        id,
        reconcile: ({ operator, value }) => {
            const selectValues = getSelectValues(items);

            const filteredValue = value.filter(value => selectValues.some(item => item === value));

            if (
                !hasValue(filteredValue) ||
                !isValidOperator(operator, ['=']) ||
                !isValuesOfValueType(filteredValue, items)
            ) {
                return null;
            }

            if (isSingleSelect && value.length > 1) {
                return null;
            }

            return { value: filteredValue };
        },
        validate: ({ value }) => {
            if (filterProps.isRequired) {
                return validateNotEmpty(value);
            }
            return null;
        },
        render: props => (
            <SelectListFilter
                {...props}
                items={items}
                isRequired={filterProps.isRequired}
                isSingleSelect={isSingleSelect}
                id={id}
            />
        ),
        onlyOneFilterItem: true,
        hasValue: ({ value }) => value.length > 0,
        getDefaultValue: () => ({ value: [] }),
        getDescription: ({ value }) => {
            const selectedItems = compact(
                items.map(item => {
                    if (isListBoxSection(item) || !value.includes(item.value)) {
                        return;
                    }

                    if (item.label) {
                        return item.label;
                    }

                    if (isString(item.content)) {
                        return item.content;
                    }

                    return item.value;
                })
            );
            return `${filterProps.title}: ${selectedItems.join(', ')}`;
        },
    };
}

interface SelectListFilterProps<ValueType extends string> extends FilterRenderProps<ValueType[]> {
    items: FilterSelectListProps<ValueType>['items'];
    isSingleSelect?: boolean;
    isDisabled?: boolean;
    /** Обязатлеьный фильтр, не может быть удален */
    isRequired?: boolean;
}

/*
 * Разворачивает список элементов (включая секции) в плоский массив опций.
 * Необходимо для корректного рендера на мобильной платформе, где секции не поддерживаются нативно.
 */
function flattenItems<ValueType extends string>(
    items: FilterSelectListProps<ValueType>['items']
): ListBoxBaseOption<ValueType>[] {
    return items.flatMap(item => (isListBoxSection(item) ? item.options : [item]));
}

function SelectListFilter<ValueType extends string>({
    id,
    title,
    addonLeft,
    value,
    color = 'normal',
    onRemove,
    onChange,
    items,
    isSingleSelect,
    wasAutoFocused,
    onFocus,
    isDisabled,
    isRemovable,
    errors,
    isRequired,
    'data-testid': dataTestId = 'SelectListFilter',
}: SelectListFilterProps<ValueType>) {
    const nodeInfo = useMemo(
        () => ({
            name: 'SelectListFilter',
            attrs: {
                id,
            },
        }),
        [id]
    );
    const { LogProvider } = useLogger(nodeInfo);

    const [isOpen, setIsOpen] = useState(!wasAutoFocused && value.length === 0);
    const onOpen = useCallback(() => setIsOpen(true), [setIsOpen]);
    const onClose = useCallback(() => setIsOpen(false), [setIsOpen]);

    const onSingleChange = useCallback((value: ValueType) => onChange({ value: [value] }), [onChange]);
    const onMultiChange = useCallback((value: ValueType[]) => onChange({ value }), [onChange]);

    useEffect(() => {
        if (!wasAutoFocused) {
            onFocus();
        }
    }, [onFocus, wasAutoFocused]);

    const clearValue = useCallback(() => onChange({ value: [] }), [onChange]);

    const optionsMobile: RadioboxItem<ValueType>[] = useMemo(() => {
        return flattenItems(items).map(item => ({
            value: item.value,
            content: item.content,
            caption: item.caption,
        }));
    }, [items]);

    const handleCheckboxChange = useCallback(
        (item: ListBoxItem<ValueType>) => (isSelected: boolean) => {
            if (!('value' in item)) {
                return;
            }
            if (isSelected) {
                onChange({ value: [...value, item.value] });
            } else {
                onChange({
                    value: value.filter(valueItem => valueItem !== item.value),
                });
            }
        },
        [onChange, value]
    );

    const handleRadioChange = useCallback(
        (value: ValueType) => {
            onChange({ value: [value] });
        },
        [onChange]
    );

    if (isMobile()) {
        return (
            <LogProvider>
                <VStack data-testid={`${dataTestId}.Values`} gap="16">
                    {isSingleSelect ? (
                        <Radiobox
                            data-testid={`${dataTestId}.Radiobox`}
                            orientation="vertical"
                            options={optionsMobile}
                            onChange={handleRadioChange}
                            value={value?.[0] || null}
                        />
                    ) : (
                        <>
                            {flattenItems(items).map(item => (
                                <Checkbox
                                    data-testid={`${dataTestId}.Checkbox.${item.value}`}
                                    key={item.value}
                                    value={item.value}
                                    size="m"
                                    caption={item.caption}
                                    isSelected={value.includes(item.value)}
                                    onChange={handleCheckboxChange(item)}
                                >
                                    {item.content}
                                </Checkbox>
                            ))}
                        </>
                    )}
                </VStack>
            </LogProvider>
        );
    }

    return (
        <LogProvider>
            {isSingleSelect ? (
                <FilterSelectList<ValueType>
                    size="xs"
                    color={color}
                    isOpen={isOpen}
                    errors={errors}
                    onOpen={onOpen}
                    onClose={onClose}
                    placeholder={title}
                    addonLeft={addonLeft}
                    value={value[0]}
                    items={items}
                    onRemove={isRequired ? clearValue : onRemove}
                    isRequired={isRequired}
                    isRemovable={isRemovable}
                    onChange={onSingleChange}
                    data-testid={dataTestId}
                    isDisabled={isDisabled}
                    logNode="FilterSelectList"
                />
            ) : (
                <FilterSelectList<ValueType>
                    size="xs"
                    color={color}
                    isOpen={isOpen}
                    errors={errors}
                    onOpen={onOpen}
                    onClose={onClose}
                    placeholder={title}
                    addonLeft={addonLeft}
                    value={value}
                    items={items}
                    onRemove={isRequired ? clearValue : onRemove}
                    isRequired={isRequired}
                    isRemovable={isRemovable}
                    onChange={onMultiChange}
                    isDisabled={isDisabled}
                    data-testid={dataTestId}
                    logNode="FilterSelectList"
                />
            )}
        </LogProvider>
    );
}
